import { Localized, useLocalization } from "@fluent/react"
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Stack, TextField } from "@mui/material"
import { useCallback, useState } from "react"
import { AppTextFieldHandler } from "../../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { URSRequirement, updateURSRequirement } from "../UrsSlice"
import { RichTextEditor } from "../../../app/RichTextEditor"
import { RequirementNote } from "../../ursOffer/ursOfferSlice"
import { selectLoggedUserId } from "../../user/userSlice"
import { SaveButton } from "../../../app/components/SaveButton"
import { CancelButton } from "../../../app/components/CancelButton"

export interface SimpleEditUrsRequirementDialogProps {
    requirement: URSRequirement
    onSuccess: (updated: URSRequirement) => void;
    onCancel: () => void
}

export const SimpleEditUrsRequirementDialog = (props: SimpleEditUrsRequirementDialogProps) => {
    const { requirement, onSuccess, onCancel } = props
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()

    // SELECTORS 
    const entity = requirement
    const { criticality: defCriticality, typeId: defTypeId, name: defName, group: defGroup, order: defOrder, note: defNote, comment: defComment } = entity
    const currentUserId = useAppSelector(selectLoggedUserId)

    // STATE 
    const [criticality, setCriticality] = useState(defCriticality)
    const [name, setName] = useState(defName)
    const [saving, setSaving] = useState(false)
    const [note, setNote] = useState<RequirementNote | null>(defNote)
    const [comment, setComment] = useState<string | null>(defComment)

    // HANDLERS 
    const handleCriticalityChange: AppTextFieldHandler = useCallback(e => setCriticality(e.target.value), [setCriticality])
    const handleOkClick = async () => {
        setSaving(true)
        const payload = {
            ...entity,
            name,
            criticality,
            sectionGuid: entity.sectionGuid,
            note,
            comment,
        };

        try {
            await dispatch(updateURSRequirement(payload)).unwrap();

            onSuccess(payload);
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    }

    return <Dialog open={true} fullWidth maxWidth="md">
        <DialogTitle
            sx={theme => ({
                bgcolor: theme.palette.grey[100],
                borderBottom: `1px solid ${theme.palette.divider}`,
                fontWeight: 600,
            })}
        >
            <Localized id="requirements-edit">
                <span>Edycja wymagania</span>
            </Localized>
        </DialogTitle>
        <DialogContent dividers>
            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <Stack direction="column" spacing={2}>
                        <TextField
                            value={criticality}
                            fullWidth
                            required={true}
                            select
                            label={<Localized id="urs-item-criticality"><span>Criticality</span></Localized>}
                            onChange={handleCriticalityChange}
                        >

                            <MenuItem value={"E"}><Localized id="urs-item-criticality-e"><span>Essential</span></Localized></MenuItem>
                            <MenuItem value={"D"}><Localized id="urs-item-criticality-d"><span>Desired</span></Localized></MenuItem>
                            <MenuItem value={"I"}><Localized id="urs-item-criticality-i"><span>Optional</span></Localized></MenuItem>
                        </TextField>
                        <RichTextEditor label={l10n.getString("urs-requirement-req", null, "_requirement")} value={name} onValueChange={setName} />
                    </Stack>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <SaveButton loading={saving} onClick={handleOkClick}></SaveButton>
            <CancelButton loading={saving} onClick={onCancel}></CancelButton>
        </DialogActions></Dialog>
}

